import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AddButton, Field, LiveScreen, QueryState, Select, StatusPill, formatDate, formatNumber, todayIso, type Column } from '@/components/live';
import { DataGrid } from '@/components/DataGrid';
import { StatCards, applyStat } from '@/components/StatCards';
import { useRows } from '@/hooks/useResource';
import { RecordDrawer } from '@/components/RecordDrawer';
import { DeleteRecordButton } from '@/components/DeleteRecordButton';
import { MapPin, Pencil } from 'lucide-react';
import { saveRow } from '@/api/resources';
import { useAuth } from '@/auth/AuthProvider';
import { can } from '@/auth/session';
import { toast } from '@/lib/toast';
import { RiskMarking } from '@/screens/parts/RiskMarking';

interface RiskRow {
  id: string; plot_id: string; plot_code: string | null; plot_no: string | null; plot_name: string | null;
  farmer_name: string | null; farmer_code: string | null; village_name: string | null;
  type_label: string | null; asset_group: string; name: string | null; description: string | null;
  severity_or_sensitivity: string | null; mitigation_status: string | null; buffer_m: number | null;
  area_ha: number | null; length_m: number | null; condition_status: string | null;
  identified_on: string | null; verified_on: string | null; source?: string | null; status?: string | null;
}

/** Groups whose features are risks to manage, not just things that are there. */
const RISK_GROUPS = ['HCV & cultural', 'Safety', 'Water'];
const SEVERITY = [{ id: 'low', label: 'Low' }, { id: 'medium', label: 'Medium' }, { id: 'high', label: 'High' }, { id: 'critical', label: 'Critical' }];
const CONDITION = [{ id: 'good', label: 'Good' }, { id: 'fair', label: 'Fair' }, { id: 'poor', label: 'Poor' }, { id: 'unusable', label: 'Unusable' }];

/**
 * Risk assessment across every farm (owner, 2026-09-27: "farm risk assessment of assets viz. HCV or
 * some thing is missing?"). The HCV and cultural sites, safety hazards, water bodies, infrastructure
 * and farm-site features marked on each plot's map, in one register: what is there, how serious,
 * what buffer it needs and what is done about it. Marked and edited on the plot's map (Plots >
 * Boundary > Mark asset / risk); a row here opens that plot.
 *
 * Its own register since 2026-09-27 (owner: "keep the risk assessment and plot page as seperate as
 * both are overlapping and wired together"): assets and risks are marked, edited and removed here,
 * on the plot's map opened in this page; Plots only shows them on its map.
 */
export default function RiskAssessmentScreen() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const mayMark = can(profile, 'plot.mapping', 'create');
  const mayEdit = can(profile, 'plot.mapping', 'update');
  // Marked here, on its own page (owner, 2026-09-27: "keep the risk assessment and plot page as
  // seperate"): the plot's map opens in this page's drawer, not on Plots.
  const [marking, setMarking] = useState<{ plotId: string | null } | null>(null);
  // ?mark=<plot id> opens the marking drawer on that plot (the Asset inventory's "Mark asset on map").
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const plot = params.get('mark');
    if (!plot) return;
    setMarking({ plotId: plot });
    const next = new URLSearchParams(params);
    next.delete('mark');
    setParams(next, { replace: true });
  }, [params, setParams]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ name: '', severity: '', mitigation: '', buffer: '', condition: '', verified: '', description: '' });
  const [stat, setStat] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [plotId, setPlotId] = useState('');
  // A row opens the asset or risk to read (owner, 2026-09-27: "the risk assessment page is having
  // navigation issue"): it used to leave for the Plots page at once. Going to the plot's map is a
  // button on the card now, taken when wanted.
  const [open, setOpen] = useState<RiskRow | null>(null);
  const rows = useRows<RiskRow>('plot_risk_register', {
    filters: { plot_id: plotId || undefined },
    search: search ? { columns: ['plot_code', 'farmer_name', 'name', 'type_label', 'asset_group', 'village_name'], term: search } : undefined,
    orderBy: { column: 'plot_code' },
    limit: 1000,
  });
  const plots = useRows<{ id: string; plot_code: string; plot_no: string | null; farmer_name: string | null }>('farm_plots', { orderBy: { column: 'plot_code' } });

  const STATS = useMemo(() => [
    { key: 'all', label: 'Assets & risks', tone: 'neutral' as const, match: () => true },
    { key: 'hcv', label: 'HCV & cultural', tone: 'ok' as const, match: (row: RiskRow) => row.asset_group === 'HCV & cultural' },
    { key: 'safety', label: 'Safety hazards', tone: 'error' as const, match: (row: RiskRow) => row.asset_group === 'Safety' },
    { key: 'water', label: 'Water', tone: 'info' as const, match: (row: RiskRow) => row.asset_group === 'Water' },
    { key: 'high', label: 'High severity', tone: 'error' as const, match: (row: RiskRow) => row.severity_or_sensitivity === 'high' },
    // A risk with no measure written against it is the one an auditor stops on.
    { key: 'unmitigated', label: 'No mitigation recorded', tone: 'warn' as const,
      match: (row: RiskRow) => RISK_GROUPS.includes(row.asset_group) && !row.mitigation_status?.trim() },
    { key: 'unverified', label: 'Not verified', tone: 'info' as const, match: (row: RiskRow) => !row.verified_on },
  ], []);
  const visible = useMemo(() => applyStat(rows.data ?? [], STATS, stat), [rows.data, STATS, stat]);

  function startEdit(row: RiskRow) {
    setDraft({ name: row.name ?? '', severity: row.severity_or_sensitivity ?? '', mitigation: row.mitigation_status ?? '',
      buffer: row.buffer_m == null ? '' : String(row.buffer_m), condition: row.condition_status ?? '', verified: row.verified_on ?? '', description: row.description ?? '' });
    setEditing(true);
  }
  async function saveEdit() {
    if (!open) return;
    try {
      await saveRow('plot_features', open.id, {
        name: draft.name.trim() || null, severity_or_sensitivity: draft.severity || null, mitigation_status: draft.mitigation.trim() || null,
        buffer_m: draft.buffer.trim() === '' ? null : Number(draft.buffer), condition_status: draft.condition || null,
        verified_on: draft.verified || null, description: draft.description.trim() || null,
      });
      await queryClient.invalidateQueries({ queryKey: ['plot_risk_register'] });
      await queryClient.invalidateQueries({ queryKey: ['plot_features'] });
      toast.success('Saved.');
      setEditing(false);
      setOpen(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'That could not be saved.');
    }
  }

  const columns: Array<Column<RiskRow>> = [
    { key: 'plot_code', label: 'Plot', render: (row) => (
      <span className="cell-stack"><strong>{row.plot_code ?? '--'}{row.plot_no ? ` · ${row.plot_no}` : ''}</strong>
        <small className="muted">{[row.farmer_name, row.village_name].filter(Boolean).join(' · ')}</small></span>
    ) },
    { key: 'asset_group', label: 'Group', render: (row) => row.asset_group },
    { key: 'name', label: 'Asset / risk', render: (row) => (
      <span className="cell-stack"><strong>{row.name ?? row.type_label ?? '--'}</strong>
        {row.type_label && row.name && <small className="muted">{row.type_label}</small>}</span>
    ) },
    { key: 'severity_or_sensitivity', label: 'Severity', render: (row) => (row.severity_or_sensitivity ? <StatusPill value={row.severity_or_sensitivity} /> : <span className="muted">Not rated</span>) },
    { key: 'buffer_m', label: 'Buffer', align: 'right', render: (row) => (row.buffer_m ? `${formatNumber(row.buffer_m)} m` : '--') },
    { key: 'size', label: 'Extent', align: 'right', render: (row) => (row.area_ha ? `${formatNumber(row.area_ha, 3)} ha` : row.length_m ? `${formatNumber(row.length_m)} m` : 'point') },
    { key: 'mitigation_status', label: 'Mitigation', render: (row) => row.mitigation_status
      ?? (RISK_GROUPS.includes(row.asset_group) ? <span className="field-error">none recorded</span> : <span className="muted">--</span>) },
    { key: 'identified_on', label: 'Identified', render: (row) => formatDate(row.identified_on) },
    { key: 'verified_on', label: 'Verified', render: (row) => (row.verified_on ? formatDate(row.verified_on) : <span className="muted">Not yet</span>) },
  ];

  return (
    <LiveScreen actions={mayMark ? <AddButton label="Mark asset / risk" onClick={() => setMarking({ plotId: plotId || null })} /> : undefined}
                subtitle="HCV and cultural sites, safety hazards, water and infrastructure marked on each farm, with their severity and mitigation."
                title="Risk assessment">
      <div className="toolbar-row">
        <label className="field"><span>Plot</span>
          <Select onChange={(value) => { setPlotId(value); setStat(null); }}
                  options={(plots.data ?? []).map((row) => ({ id: row.id, label: [row.plot_code, row.plot_no, row.farmer_name].filter(Boolean).join(' · ') }))}
                  placeholder="All plots" value={plotId} /></label>
      </div>
      <StatCards active={stat} onSelect={setStat} rows={rows.data ?? []} stats={STATS} />
      <QueryState empty="Nothing marked yet. Use Mark asset / risk: choose the plot and mark it on its map." query={rows}>
        <DataGrid columns={columns} exportName="risk-assessment" onSearchChange={setSearch} recordNoun="asset or risk"
                  onRowClick={setOpen} selectedId={open?.id}
                  rows={visible} screenKey="plot.register" search={search} searchPlaceholder="Plot, farmer, asset or type" />
      </QueryState>
      <RecordDrawer
        footer={<>
          {open && !editing && (
            <DeleteRecordButton entity="feature" id={open.id} invalidate={['plot_risk_register', 'plot_features']}
                                label={open.name ?? open.type_label ?? 'asset'} onDeleted={() => setOpen(null)} screenKey="plot.mapping" />
          )}
          {editing ? (
            <>
              <button className="cancel" onClick={() => setEditing(false)} type="button">Cancel</button>
              <button className="primary" onClick={() => void saveEdit()} type="button">Save changes</button>
            </>
          ) : (
            <>
              <button onClick={() => setOpen(null)} type="button">Close</button>
              {open && mayMark && <button onClick={() => { const plotId = open.plot_id; setOpen(null); setMarking({ plotId }); }} type="button"><MapPin size={14} /> Mark more on this plot</button>}
              {open && mayEdit && <button className="primary" onClick={() => startEdit(open)} type="button"><Pencil size={14} /> Edit</button>}
            </>
          )}
        </>}
        onClose={() => { setOpen(null); setEditing(false); }} open={open !== null} record={open ? { table: 'ops.plot_features', id: open.id, invalidate: ['plot_risk_register'] } : null}
        subtitle={open ? [open.plot_code, open.plot_no, open.farmer_name].filter(Boolean).join(' · ') : undefined}
        title={open ? `${open.asset_group}: ${open.name ?? open.type_label ?? 'asset'}` : ''}
      >
        {open && !editing && (
          <dl className="detail-grid spec-view">
            <div><dt>Group</dt><dd>{open.asset_group}</dd></div>
            <div><dt>Type</dt><dd>{open.type_label ?? '--'}</dd></div>
            <div><dt>Severity / sensitivity</dt><dd>{open.severity_or_sensitivity ? <StatusPill value={open.severity_or_sensitivity} /> : 'Not rated'}</dd></div>
            <div><dt>Buffer</dt><dd>{open.buffer_m ? `${formatNumber(open.buffer_m)} m` : '--'}</dd></div>
            <div><dt>Extent</dt><dd>{open.area_ha ? `${formatNumber(open.area_ha, 3)} ha` : open.length_m ? `${formatNumber(open.length_m)} m` : 'A point'}</dd></div>
            <div><dt>Condition</dt><dd>{open.condition_status ?? '--'}</dd></div>
            <div className="spec-view-wide"><dt>Mitigation</dt><dd>{open.mitigation_status
              ?? (RISK_GROUPS.includes(open.asset_group) ? <span className="field-error">None recorded</span> : '--')}</dd></div>
            <div className="spec-view-wide"><dt>Description</dt><dd>{open.description ?? '--'}</dd></div>
            <div><dt>Identified</dt><dd>{open.identified_on ? formatDate(open.identified_on) : '--'}</dd></div>
            <div><dt>Verified</dt><dd>{open.verified_on ? formatDate(open.verified_on) : 'Not yet'}</dd></div>
            <div><dt>Source</dt><dd>{open.source ?? '--'}</dd></div>
            <div><dt>Village</dt><dd>{open.village_name ?? '--'}</dd></div>
          </dl>
        )}
        {open && editing && (
          <>
            <Field label="Name"><input onChange={(event) => setDraft({ ...draft, name: event.target.value })} value={draft.name} /></Field>
            <div className="field-row">
              <Field label="Severity / sensitivity"><Select onChange={(value) => setDraft({ ...draft, severity: value })} options={SEVERITY} placeholder="Not rated" value={draft.severity} /></Field>
              <Field label="Buffer to keep clear (m)"><input inputMode="decimal" onChange={(event) => setDraft({ ...draft, buffer: event.target.value })} value={draft.buffer} /></Field>
            </div>
            <Field hint="What is being done about it." label="Mitigation">
              <textarea onChange={(event) => setDraft({ ...draft, mitigation: event.target.value })} rows={2} value={draft.mitigation} />
            </Field>
            <div className="field-row">
              <Field label="Condition"><Select onChange={(value) => setDraft({ ...draft, condition: value })} options={CONDITION} placeholder="--" value={draft.condition} /></Field>
              <Field label="Verified on"><input max={todayIso()} onChange={(event) => setDraft({ ...draft, verified: event.target.value })} type="date" value={draft.verified} /></Field>
            </div>
            <Field label="Description"><textarea onChange={(event) => setDraft({ ...draft, description: event.target.value })} rows={2} value={draft.description} /></Field>
            <p className="muted">Its shape on the map is changed by removing it and marking it again.</p>
          </>
        )}
      </RecordDrawer>
      {marking && <RiskMarking onClose={() => { setMarking(null); void queryClient.invalidateQueries({ queryKey: ['plot_risk_register'] }); }} plotId={marking.plotId} />}
    </LiveScreen>
  );
}
